import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, input, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_HEADER_ACTIONS, FloatingPanelHeaderAction } from '@shared/floating-panel/floating-panel-header-actions.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { FLOATING_PANEL_TOPBAR_LEFT } from '@shared/floating-panel/floating-panel-topbar-content.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../planet-grid/hd-planet-display-config';
import { HdGateInfoFullComponent } from '../gate-info/variants/hd-gate-info-full.component';
import { HdGateInfoSmallComponent } from '../gate-info/variants/hd-gate-info-small.component';
import { HdGateInfoRequestedComponent } from '../gate-info/variants/hd-gate-info-requested.component';
import { HdGateInfoRequestedContext } from '../gate-info/hd-gate-info-requested-context.model';
import { HdDocPanelService } from '../doc-panel/hd-doc-panel.service';
import { HdDocRef } from '../doc-panel/hd-doc-ref.model';
import { HdDocViewerComponent } from '../doc-panel/hd-doc-viewer.component';
import { HdEmotionalWaveRowComponent } from '../../emotional-waves/hd-emotional-wave-row.component';
import { hdDefinedEmotionalChannelKeys } from '../../emotional-waves/hd-emotional-waves.metadata';
import {
  HdChannelInfoRequestedContext,
  buildHdChannelInfoDockLabel,
} from './hd-channel-info-requested-context.model';
import {
  HdChannelInfoSectionVm,
  HdChannelInfoVmDeps,
  buildHdChannelInfoSections,
} from './hd-channel-info.view-model';

const P = 'humanDesign.chart.channelInfo';

/**
 * Kanaal-info paneel (spec `2026-07-02-hd-kanaal-highlight-en-kanaal-info-design.md`
 * §6.3). Twee lokale weergaven:
 *  - kanaal-weergave (default): één sectie per defined kanaal met naam,
 *    "een design van", circuit-chip en de planeetactivaties-lijst;
 *  - poort-weergave: de bestaande gate-info-inhoud (klein/volledig) voor een
 *    aangeklikte poort, met een terugknop bovenaan én onderaan.
 *
 * Dit component is de EIGENAAR van zijn floating panel: de header-actie
 * (vergroot/verklein) wisselt in kanaal-weergave de brede modus en in
 * poort-weergave de kleine/volledige poort-info. De embedded gate-info
 * varianten blijven van de panel-tokens af (regressie 2026-07-03).
 * Alle teksten volgen live de content-taal (NL/EN).
 */
@Component({
  selector: 'hd-channel-info-requested',
  templateUrl: './hd-channel-info-requested.component.html',
  styleUrl: './hd-channel-info-requested.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateInfoSmallComponent, HdGateInfoFullComponent, HdDocViewerComponent, HdEmotionalWaveRowComponent],
  providers: [
    // Shield: embedded varianten erven de floating-panel-tokens van dít
    // paneel; de topbar hoort bij het kanaal-paneel zelf en mag niet door
    // een embedded full-variant gevuld worden (zelfde DI-erfenis-klasse als
    // de replaceData-regressie van 2026-07-03).
    { provide: FLOATING_PANEL_TOPBAR_LEFT, useValue: null },
  ],
  host: {
    // Vaste brede breedte enkel in de KANAAL-weergave; in de poort-weergave
    // bepaalt de embedded variant zelf zijn breedte (identiek aan losse
    // poort-panelen — een vaste host-breedte gaf daar een h-scrollbar).
    '[class.hd-channel-info-host--wide]': 'isLarge() && activeGateContext() === null',
    '[class.hd-channel-info-host--gate-view]': 'activeGateContext() !== null',
    '[class.hd-channel-info-host--doc-view]': 'activeChannelDocRef() !== null',
    '[class.hd-channel-info-host--dock-full]': 'isDockFull',
  },
})
export class HdChannelInfoRequestedComponent {
  /** Context via input (direct gebruik) of via het floating-panel data-token. */
  readonly context = input<HdChannelInfoRequestedContext | null>(null);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdChannelInfoRequestedContext>
    | null;

  /** Ref naar het eigen floating panel (afwezig in gedockte modus). */
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });

  /** Header-actions-strook van het eigen paneel (enkel floating aanwezig). */
  private readonly headerActionsSignal = inject(FLOATING_PANEL_HEADER_ACTIONS, { optional: true });

  /** Dock-zone waarin dit paneel gerenderd wordt (enkel gedockt aanwezig). */
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });

  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly docPanel = inject(HdDocPanelService);

  protected readonly effectiveContext = computed<HdChannelInfoRequestedContext | null>(() => {
    const context = this.context() ?? this.panelData?.() ?? null;
    // Kind-guard als vangnet: mocht de paneel-data ooit een niet-kanaal-context
    // bevatten (bv. door een vreemde replaceData — regressie 2026-07-03), render
    // dan leeg i.p.v. in een change-detection-crashloop te belanden.
    return context?.kind === 'channel-requested' ? context : null;
  });

  /** Composite chart: chart 1 (de basisrijen) kleurt natal-blauw i.p.v. rood/zwart. */
  protected readonly isCompositeBaseStyle = computed(
    () => this.effectiveContext()?.baseStyle === 'composite',
  );

  /** `null` = kanaal-weergave; poortnummer = embedded poort-weergave. */
  protected readonly activeGateView = signal<number | null>(null);

  protected readonly activeGateContext = computed<HdGateInfoRequestedContext | null>(() => {
    const gate = this.activeGateView();
    if (gate == null) return null;
    return this.effectiveContext()?.gateContexts[gate] ?? null;
  });

  /** Kanaaldocument dat in-place wordt getoond; blijft geldig na contextrefresh. */
  private readonly activeChannelDocKey = signal<string | null>(null);
  protected readonly activeChannelDocRef = computed<HdDocRef | null>(() => {
    const channelKey = this.activeChannelDocKey();
    const context = this.effectiveContext();
    if (!channelKey || !context?.channels.some(channel => channel.channelKey === channelKey)) return null;
    return { kind: 'channel', channelKey };
  });

  /**
   * Eén gedeelde paneel-modus voor beide weergaven: `large` = brede
   * kanaal-weergave én volledige poort-info. De modus blijft staan bij het
   * wisselen kanaal ↔ poort zodat de gekozen werkmodus behouden blijft.
   */
  protected readonly panelMode = signal<'compact' | 'large'>('compact');
  private panelModeInitialized = false;

  /** Gedockt in de full-zone → de dock-host bepaalt de breedte. */
  protected readonly isDockFull = this.dockRenderZone === 'full';

  /** Large-weergave: brede host (width-cap van de volle poort-dialog) + volledige poort-info. */
  protected readonly isLarge = computed(() => this.isDockFull || this.panelMode() === 'large');

  constructor() {
    // Eénmalige init van de paneel-modus uit de context (analoog aan de
    // initialMode-init van de gate-info router): full/full-docked-*
    // open-gedrag → meteen large.
    effect(() => {
      const ctx = this.effectiveContext();
      if (this.panelModeInitialized || !ctx) return;
      this.panelModeInitialized = true;
      this.panelMode.set(ctx.initialWide ? 'large' : 'compact');
    });

    // Header-actie van het eigen paneel: één compact/large-toggle die in
    // beide weergaven dezelfde paneel-modus stuurt.
    effect(() => {
      if (!this.headerActionsSignal) return;
      const lang = this.contentLang.effectiveLang();
      const action: FloatingPanelHeaderAction = this.panelMode() === 'large'
        ? {
          icon: 'down-left-and-up-right-to-center',
          tooltip: this.transloco.translate('humanDesign.gateInfo.collapse', undefined, lang),
          onClick: () => this.shrink(),
        }
        : {
          icon: 'up-right-and-down-left-from-center',
          tooltip: this.transloco.translate('humanDesign.gateInfo.expand', undefined, lang),
          onClick: () => this.enlarge(),
        };
      this.headerActionsSignal.set([action]);
    });
  }

  protected readonly sections = computed<readonly HdChannelInfoSectionVm[]>(() => {
    const context = this.effectiveContext();
    if (!context) return [];
    // Taal als expliciete dependency zodat een taalswitch de VM herbouwt.
    const lang = this.contentLang.effectiveLang();
    return buildHdChannelInfoSections(context, this.buildDeps(lang));
  });

  /**
   * Gedefinieerde emotionele kanalen van de chart, voor de badges in het
   * golvenpaneel. De kanaalcontext zelf draagt enkel de activaties van de
   * eigen poorten; alle chart-activaties zitten in de poort-contexten.
   */
  protected readonly definedEmotionalChannelKeys = computed<readonly string[] | undefined>(() => {
    const gateContexts = Object.values(this.effectiveContext()?.gateContexts ?? {});
    const allActivations = gateContexts.find(gateContext => gateContext.allActivations)?.allActivations;
    return allActivations ? hdDefinedEmotionalChannelKeys(allActivations) : undefined;
  });

  protected readonly planetActivationsTitle = computed(() => this.t(`${P}.planetActivations`));
  protected readonly noActivationsLabel = computed(() => this.t(`${P}.noActivations`));
  protected readonly backAriaLabel = computed(() => this.t(`${P}.backToChannelAria`));
  protected readonly backLabel = computed(() => {
    const context = this.effectiveContext();
    const keys = context ? context.channels.map(entry => entry.channelKey) : [];
    return this.t(`${P}.backToChannel`, { keys: buildHdChannelInfoDockLabel(keys) });
  });

  /**
   * Positie van de kanaal-weergave op het moment dat er naar een poort werd
   * doorgeklikt. Bij {@link backToChannel} wordt hiervan enkel de **hoogte**
   * hersteld (de korte kanaal-dialog mag niet op de hoger geklemde poort-hoogte
   * blijven hangen); de **horizontale** positie volgt de plek waar de poort op
   * dat moment staat, zodat een in poort-weergave weggesleepte dialog niet
   * horizontaal terugspringt.
   */
  private channelViewPosition: { x: number; y: number } | null = null;

  /**
   * Key van de poort die — in de "apart in dock"-modus — als los paneel vanuit
   * DEZE kanaaldialog geopend werd. Een klik op een andere poort sluit de
   * vorige eerst (vervang-gedrag per kanaaldialog).
   */
  private separateGateKey: string | null = null;

  /** Of er detailinfo (een poort-context) beschikbaar is om te openen voor deze poort. */
  protected canOpenGate(gate: number): boolean {
    return this.effectiveContext()?.gateContexts[gate] != null;
  }

  protected openGate(gate: number): void {
    const context = this.effectiveContext();
    const gateContext = context?.gateContexts[gate];
    if (!context || gateContext == null) return;
    // "Apart in dock"-modus: open de poort als los paneel en laat de
    // kanaal-weergave ongewijzigd staan (géén in-place poort-weergave).
    if (context.gateClickOpensSeparateDock) {
      this.openGateInSeparateDock(gate, gateContext, context);
      return;
    }
    // Onthoud de huidige kanaal-positie vóór de nudge zodat de terugknop hem
    // exact kan herstellen (no-op gedockt of zonder eigen panel-ref).
    this.channelViewPosition = this.panelRef?.getPosition() ?? null;
    this.activeChannelDocKey.set(null);
    // Bewust géén modus-reset: de paneel-modus (compact/large) reist mee
    // tussen kanaal- en poort-weergave.
    this.activeGateView.set(gate);
    // De poort-inhoud is (veel) hoger dan de kanaal-weergave. Bewust GÉÉN
    // recenter: het paneel moet op de positie blijven waar het kanaal stond
    // (respecteert een handmatig weggesleepte dialog). `nudgeIntoView` meet ná
    // de render (rAF) en schuift enkel omhoog wanneer de poort anders onderaan
    // uit beeld zou vallen.
    this.panelRef?.nudgeIntoView();
  }

  protected backToChannel(): void {
    this.activeGateView.set(null);
    this.activeChannelDocKey.set(null);
    const original = this.channelViewPosition;
    if (!original) return;
    // Herstel enkel de originele hoogte (de korte kanaal-weergave past terug op
    // zijn eerdere, lagere plek), maar behoud de huidige horizontale positie
    // zodat een in poort-weergave weggesleepte dialog horizontaal blijft staan.
    const currentX = this.panelRef?.getPosition()?.x ?? original.x;
    this.panelRef?.moveTo({ x: currentX, y: original.y });
  }

  /** Opent de volledige markdown-info van één kanaal volgens de gedeelde detailsetting. */
  protected openChannelDocument(channelKey: string): void {
    const context = this.effectiveContext();
    if (!context?.channels.some(channel => channel.channelKey === channelKey)) return;

    const ref: HdDocRef = { kind: 'channel', channelKey };
    if (context.gateClickOpensSeparateDock) {
      this.docPanel.openDocPanel(ref);
      return;
    }

    this.channelViewPosition = this.panelRef?.getPosition() ?? null;
    this.activeGateView.set(null);
    this.activeChannelDocKey.set(channelKey);
    this.panelRef?.nudgeIntoView();
  }

  protected fullInfoAriaLabel(channelKey: string): string {
    return this.t(`${P}.openFullInfoAria`, { channel: channelKey });
  }

  protected gateBadgeAriaLabel(gate: number): string {
    return this.t(`${P}.openGateAria`, { gate });
  }

  /** Naar large: brede kanaal-weergave + volledige poort-info. */
  protected enlarge(): void {
    this.panelMode.set('large');
    // Fors groter → behoud de positie en klem enkel terug in beeld wanneer het
    // vergrote paneel anders onderaan/rechts uit beeld zou vallen (no-op
    // gedockt of zonder eigen panel-ref).
    this.panelRef?.nudgeIntoView();
  }

  protected shrink(): void {
    this.panelMode.set('compact');
  }

  /**
   * Opent de poort als een apart floating/gedockt paneel (dezelfde
   * gate-info-router als losse poort-panelen), terwijl deze kanaaldialog
   * ongewijzigd blijft staan. Dezelfde dedupe-key als de page-panelen
   * (`gate-info:<gate>`) zodat een reeds open poort gefocust wordt i.p.v.
   * gedupliceerd. Vervang-gedrag: een klik op een ándere poort vanuit dezelfde
   * kanaaldialog sluit de vorige zo geopende poort eerst.
   */
  private openGateInSeparateDock(
    gate: number,
    gateContext: HdGateInfoRequestedContext,
    context: HdChannelInfoRequestedContext,
  ): void {
    // Opgeloste key: een op een chart vergrendeld poortpaneel wordt niet
    // overschreven, er opent een volgend paneel naast (resolveKey).
    const key = this.floatingPanels.resolveKey(`gate-info:${gate}`);
    // Een intussen vergrendeld paneel blijft staan (bewuste keuze).
    const previousLocked = this.floatingPanels.panels()
      .some(panel => panel.key === this.separateGateKey && panel.lockedTo() !== null);
    if (this.separateGateKey && this.separateGateKey !== key && !previousLocked) {
      this.floatingPanels.closeForKey(this.separateGateKey);
    }
    // Kern van deze modus: de poort landt altijd gedockt in het side-panel
    // (los van het floating/docked page-open-gedrag), zodat hij náást de
    // blijvende kanaal-dialog in de rechter sidepanel verschijnt.
    this.floatingPanels.openOrFocus<HdGateInfoRequestedComponent, HdGateInfoRequestedContext>({
      key,
      component: HdGateInfoRequestedComponent,
      data: gateContext,
      dockTab: { label: `${gate}` },
      dockZone: context.separateDockZone ?? 'small',
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
    this.separateGateKey = key;
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params, this.contentLang.effectiveLang());
  }

  private buildDeps(lang: string): HdChannelInfoVmDeps {
    return {
      channelTranslation: channelKey => this.gateInfoService.channelTranslationIn(channelKey, lang) ?? undefined,
      circuitFor: channelKey => this.gateInfoService.channelMainCircuit(channelKey),
      circuitName: circuitKey => this.gateInfoService.circuitNameIn(circuitKey, lang),
      centerLabel: center => this.gateInfoService.centerLabelIn(center, lang),
      gateKeynotes: gate => {
        const translations = this.gateInfoService.gateInfoIn(gate, lang).translations;
        return { keynote1: translations.keynote1, keynote2: translations.keynote2 };
      },
      lineKeynote: (gate, line) =>
        this.gateInfoService.gateInfoIn(gate, lang).translations.lines[line - 1]?.keynote ?? '',
      planetName: planetId => {
        const config = getHdPlanetDisplayConfigByPlanetId(planetId);
        if (!config) return `#${planetId}`;
        return this.planetInfoService.planetInfoIn(config.key, lang).name ?? config.label;
      },
      planetSubtitle: planetId => {
        const config = getHdPlanetDisplayConfigByPlanetId(planetId);
        if (!config) return '';
        return this.planetInfoService.planetInfoIn(config.key, lang).subtitle ?? '';
      },
      planetGlyph: planetId => getHdPlanetDisplayConfigByPlanetId(planetId)?.astronomiconChar ?? '',
      t: (key, params) => this.transloco.translate(key, params, lang),
    };
  }
}
